import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatMoney, getCurrency, DEFAULT_CURRENCY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = no user, object = user
  const [business, setBusiness] = useState(null);
  const [error, setError] = useState("");

  const loadBusiness = useCallback(async () => {
    try {
      const r = await api.get("/business/me");
      setBusiness(r.data || null);
    } catch {
      setBusiness(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get("/auth/me")
      .then(async (res) => {
        if (!mounted) return;
        setUser(res.data);
        await loadBusiness();
      })
      .catch(() => { if (mounted) setUser(false); });
    return () => { mounted = false; };
  }, [loadBusiness]);

  // Show "Made with Emergent" badge ONLY to the Application Owner
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (!badge) return;
    if (user && user.is_app_owner) {
      badge.style.setProperty("display", "inline-flex", "important");
    } else {
      badge.style.setProperty("display", "none", "important");
    }
  }, [user]);

  const login = async (identifier, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      setUser(data);
      await loadBusiness();
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async () => {
    setError("Public registration is disabled — please contact your admin.");
    return false;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    setUser(false);
    setBusiness(null);
  };

  const refresh = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      await loadBusiness();
    } catch {
      setUser(false);
      setBusiness(null);
    }
  };

  // Resolve active currency for display: prefer business.currency_symbol, else lookup by code, else default (PKR).
  const currency = useMemo(() => {
    if (!business) return DEFAULT_CURRENCY;
    const code = business.currency_code || "";
    const symbol = business.currency_symbol || "";
    if (symbol) {
      const matched = getCurrency(code);
      return { ...matched, code: code || matched.code, symbol };
    }
    return getCurrency(code);
  }, [business]);

  const fmt = useCallback((n) => formatMoney(n, currency.symbol, currency.locale), [currency]);

  const value = useMemo(
    () => ({
      user, login, register, logout, refresh, error, setError,
      business, reloadBusiness: loadBusiness,
      currency, fmt,
      // Admin and Manager share full administrative access (Manager === Admin equivalent)
      isAdmin: !!(user && (user.role === "admin" || user.role === "manager")),
      isManager: !!(user && user.role === "manager"),
      isEditor: !!(user && (user.role === "admin" || user.role === "manager" || user.role === "editor")),
      isViewer: !!(user && user.role === "viewer"),
      isAppOwner: !!(user && user.is_app_owner),
    }),
    [user, error, business, currency, fmt, loadBusiness]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
